import { useBillingServiceStore } from '@billing/AppProvider'
import { VesselReportContainerEntry } from '@billing/app/api'
import exportService from '@billing/services/ExportService'
import { SwapHoriz } from '@mui/icons-material'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import SouthEastIcon from '@mui/icons-material/SouthEast'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
  YardIcon,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useEffect, useMemo } from 'react'
import { ReportSummaryCard } from '../reportSummaryCard/ReportSummaryCard'

export const VesselReport = observer(() => {
  const { t } = useTranslate()

  const { vesselReportUIStore } = useBillingServiceStore()

  const dataExportConfig = useMemo<IDataExportConfig<VesselReportContainerEntry>>(
    () => ({
      containerNumber: { header: t('containerNumber', 'Container Number'), type: 'string' },
      operationType: { header: t('operation', 'Operation'), type: 'string' },
      operator: { header: t('operator', 'Operator'), type: 'string' },
      customerAbbreviation: { header: t('customer', 'Customer'), type: 'string' },
      isoCode: { header: t('isoCode', 'ISO Code'), type: 'string' },
      unitStatus: { header: t('unitStatus', 'Unit Status'), type: 'string' },
      length: { header: t('length', 'Length'), type: 'string' },
      weight: { header: t('weight', 'Weight'), type: 'number' },
      isEmpty: { header: t('status', 'Status'), type: 'string' },
      isTransshipment: { header: t('transshipment', 'Transshipment'), type: 'string' },
      isDangerous: { header: t('dangerous', 'Dangerous'), type: 'string' },
      isDamaged: { header: t('damaged', 'Damaged'), type: 'string' },
      isPluggedIn: { header: t('pluggedIn', 'Plugged In'), type: 'string' },
      isOOG: { header: t('oog', 'OOG'), type: 'string' },
      entryDateTime: { header: t('entryDate', 'Entry Date'), type: 'date' },
      workInstructionFinishedAt: { header: t('finishedAt', 'Finished At'), type: 'date' },
      in: { header: t('in', 'In'), type: 'date' },
      out: { header: t('out', 'Out'), type: 'date' },
    }),
    [t],
  )

  const columns = useMemo<MRT_ColumnDef<VesselReportContainerEntry>[]>(
    () => [
      {
        id: 'containerNumber',
        header: t('containerNumber', 'Container Number'),
        accessorKey: 'containerNumber',
        accessorFn: row => row.containerNumber,
        size: 200,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'operationType',
        header: t('operation', 'Operation'),
        accessorFn: row => row.operationType,
        size: 120,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'operator',
        header: t('operator', 'Operator'),
        accessorFn: row => row.operator,
        size: 150,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'customerAbbreviation',
        header: t('customer', 'Customer'),
        accessorFn: row => row.customerAbbreviation,
        size: 120,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isoCode',
        header: t('isoCode', 'ISO Code'),
        accessorFn: row => row.isoCode,
        size: 100,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'unitStatus',
        header: t('unitStatus', 'Unit Status'),
        accessorFn: row => row.unitStatus,
        size: 120,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'length',
        header: t('length', 'Length'),
        accessorFn: row => row.length,
        size: 100,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'weight',
        header: t('weight', 'Weight'),
        accessorFn: row => row.weight,
        size: 120,
        Cell: ({ cell }) =>
          cell.getValue<number>() ? `${cell.getValue<number>()?.toLocaleString()} kg` : '-',
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'isEmpty',
        header: t('status', 'Status'),
        accessorFn: row => (row.isEmpty ? t('empty', 'Empty') : t('full', 'Full')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isTransshipment',
        header: t('transshipment', 'Transshipment'),
        accessorFn: row => (row.isTransshipment ? t('yes', 'Yes') : t('no', 'No')),
        size: 120,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isDangerous',
        header: t('dangerous', 'Dangerous'),
        accessorFn: row => row.isDangerous,
        size: 100,
        Cell: ({ cell }) => (cell.getValue<boolean>() ? t('yes', 'Yes') : t('no', 'No')),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isDamaged',
        header: t('damaged', 'Damaged'),
        accessorFn: row => (row.isDamaged ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isPluggedIn',
        header: t('pluggedIn', 'Plugged In'),
        accessorFn: row => (row.isPluggedIn ? t('yes', 'Yes') : t('no', 'No')),
        size: 110,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isOOG',
        header: t('oog', 'OOG'),
        accessorFn: row => (row.isOOG ? t('yes', 'Yes') : t('no', 'No')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'entryDateTime',
        header: t('entryDate', 'Entry Date'),
        accessorFn: row => row.entryDateTime,
        size: 160,
        ...tomTableColumnFilterPresets.dateMandatory(),
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
      },
      {
        id: 'workInstructionFinishedAt',
        header: t('finishedAt', 'Finished At'),
        accessorFn: row => row.workInstructionFinishedAt,
        size: 160,
        ...tomTableColumnFilterPresets.dateOptional(),
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
      },
      {
        id: 'in',
        header: t('in', 'In'),
        accessorFn: row => row.in,
        size: 160,
        ...tomTableColumnFilterPresets.dateOptional(),
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
      },
      {
        id: 'out',
        header: t('out', 'Out'),
        accessorFn: row => row.out,
        size: 160,
        ...tomTableColumnFilterPresets.dateOptional(),
        Cell: ({ cell }) =>
          cell.getValue<string>() ? dayjs(cell.getValue<string>()).format('L LT') : EmptyIndicator,
      },
    ],
    [t],
  )

  const defaultOptions = useDefaultMRTOptions<VesselReportContainerEntry>(undefined, {
    enableDataExport: true,
    title: t('vesselReport', 'Vessel Report'),
  })

  const { savedPreferences, onPreferencesChange } =
    useTablePreferencesConfig('billing/vessel-report')

  const table = useTomTable(
    {
      columns,
      data: vesselReportUIStore.vesselContainers,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      enableRowActions: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: [...(defaultOptions.initialState?.columnPinning?.left ?? []), 'containerNumber'],
        },
      },
      muiTableContainerProps: {
        sx: {
          minHeight: '450px',
        },
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  const handleDownload = () => {
    const rows = table.getPrePaginationRowModel().rows
    const dataToExport = rows.map(row => {
      const item = row.original
      return {
        ...item,
        isEmpty: item.isEmpty ? t('empty', 'Empty') : t('full', 'Full'),
        isTransshipment: item.isTransshipment ? t('yes', 'Yes') : t('no', 'No'),
        isDangerous: item.isDangerous ? t('yes', 'Yes') : t('no', 'No'),
        isDamaged: item.isDamaged ? t('yes', 'Yes') : t('no', 'No'),
        isPluggedIn: item.isPluggedIn ? t('yes', 'Yes') : t('no', 'No'),
        isOOG: item.isOOG ? t('yes', 'Yes') : t('no', 'No'),
      }
    })

    exportToCsv(
      t('vesselReport', 'Vessel Report'),
      dataToExport,
      dataExportConfig,
      `Vessel_Report_${dayjs().format('YYYYMMDD')}.csv`,
    )
  }

  useEffect(() => {
    async function fetchData() {
      vesselReportUIStore.setVesselSummary(
        await exportService.getVesselSummary(+vesselReportUIStore.selectedVesselVisitId!),
      )
    }

    fetchData()
  }, [vesselReportUIStore])

  useEffect(() => {
    vesselReportUIStore.setHandleDownload(handleDownload)
    return () => vesselReportUIStore.setHandleDownload(handleDownload)
  }, [vesselReportUIStore])

  return (
    <Stack width='100%' gap={theme => theme.customSpacing.m}>
      <Stack direction='row' gap={theme => theme.customSpacing.m}>
        <ReportSummaryCard
          title={t('total', 'Total')}
          totalNumber={vesselReportUIStore.vesselSummaryMetrics.total}
          numberDescription={t('containers', 'Containers')}
          icon={<YardIcon />}
        />
        <ReportSummaryCard
          title={t('discharge', 'Discharge')}
          totalNumber={vesselReportUIStore.vesselSummaryMetrics.discharge}
          numberDescription={t('containers', 'Containers')}
          icon={<SouthEastIcon />}
        />
        <ReportSummaryCard
          title={t('load', 'Load')}
          totalNumber={vesselReportUIStore.vesselSummaryMetrics.load}
          numberDescription={t('containers', 'Containers')}
          icon={<NorthEastIcon />}
        />
        <ReportSummaryCard
          title={t('restow', 'Restow')}
          totalNumber={vesselReportUIStore.vesselSummaryMetrics.restow}
          numberDescription='containers'
          icon={<SwapHoriz />}
        />
      </Stack>

      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant='h6' sx={{ mb: 1 }}>
          {t('containerDetails', 'Container Details')}
        </Typography>

        {!vesselReportUIStore.isLoading ? <TomTable table={table} /> : <></>}
      </Box>
    </Stack>
  )
})
